/**
 * Turns a click in the 360 panorama into a real-world 3D point.
 *
 * The point has to come from the scan's own point cloud: the panorama shows the real site, and
 * the 3D model can be missing things the scan contains (a junction box in front of a vessel). A
 * model hit along the same ray lands on whatever modelled surface is BEHIND them. The old pick
 * returned `pointCloudHit || modelHit`, so whenever the cloud had not streamed in yet the model
 * hit was saved silently - and its "still loading" retry never ran in a modelled facility.
 *
 * Rules:
 *   1. Always load THIS scan's point cloud, even when another cloud is already open.
 *   2. Pick the point cloud only, retrying while it streams in.
 *   3. Only when the scan has no point cloud of its own, fall back to the model - with a warning.
 */

export const PANO_360_PICK_SOURCE = Object.freeze({
  POINT_CLOUD: 'point-cloud',
  MODEL: 'model',
});

// The octree streams in level by level after a load, so the pick is retried: a first try after a
// short redraw, then every PICK_INTERVAL until PICK_TIMEOUT has passed.
export const PANO_360_PICK_FIRST_WAIT_MS = 100;
export const PANO_360_PICK_INTERVAL_MS = 500;
export const PANO_360_PICK_TIMEOUT_MS = 10000;

/** Wait before each point-cloud pick: [first, interval, interval, ...] summing to <= timeout. */
export function buildPickRetryDelays(
  firstWaitMs = PANO_360_PICK_FIRST_WAIT_MS,
  intervalMs = PANO_360_PICK_INTERVAL_MS,
  timeoutMs = PANO_360_PICK_TIMEOUT_MS,
) {
  const delays = [firstWaitMs];
  let elapsed = firstWaitMs;
  while (intervalMs > 0 && elapsed + intervalMs <= timeoutMs) {
    delays.push(intervalMs);
    elapsed += intervalMs;
  }
  return delays;
}

export const PANO_360_PICK_RETRY_DELAYS_MS = Object.freeze(buildPickRetryDelays());

export const PANO_360_PICK_MESSAGES = Object.freeze({
  WAITING: 'Waiting for the point cloud to load here...',
  STILL_LOADING: 'The point cloud is still loading here. Please try again in a moment.',
  MODEL_FALLBACK: 'This scan has no point cloud, so the point was taken from the 3D model. '
    + 'It may sit behind objects that only appear in the scan.',
  NO_SURFACE: 'No point cloud or model surface was found at that spot.',
});

// OPEN_360_CORRESPONDING_PC reports this source when it fell back to a cloud that was already
// open: that cloud is not the scan's own, so a miss on it says nothing about loading.
const BORROWED_CLOUD_SOURCE = 'opened';

const emptyResult = (autoLoadedPcName) => ({ point: null, source: null, autoLoadedPcName });

/**
 * @param {Object} deps
 * @param {Function} deps.loadScanPointCloud - Resolves {pcName, wasAlreadyOpened, source}; rejects
 *   when the scan has no point cloud.
 * @param {Function} deps.pickPointCloud - Point-cloud hit for the click, or null.
 * @param {Function} deps.pickModel - Model hit for the click, or null.
 * @param {Function} deps.waitForRender - (ms) => Promise; lets the viewer redraw before a pick.
 * @param {{info: Function, warning: Function}} deps.notify - User-facing messages.
 * @param {number[]} [deps.retryDelaysMs] - Defaults to 100 ms, then every 500 ms up to 10 s.
 * @returns {Promise<{point: Object|null, source: string|null, autoLoadedPcName: string|null}>}
 *   `autoLoadedPcName` names a cloud this pick opened, so a caller that closes what it opened can.
 */
export async function pick360Point({
  loadScanPointCloud,
  pickPointCloud,
  pickModel,
  waitForRender,
  notify,
  retryDelaysMs = PANO_360_PICK_RETRY_DELAYS_MS,
}) {
  let load = null;
  try {
    load = await loadScanPointCloud();
  } catch (err) {
    load = null;
  }
  const autoLoadedPcName = load && load.wasAlreadyOpened === false && load.pcName ? load.pcName : null;

  if (load) {
    for (let attempt = 0; attempt < retryDelaysMs.length; attempt += 1) {
      await waitForRender(retryDelaysMs[attempt]);
      const point = pickPointCloud();
      if (point) return { point, source: PANO_360_PICK_SOURCE.POINT_CLOUD, autoLoadedPcName };
      // Said once, after the first miss: the retries can take up to PICK_TIMEOUT.
      if (attempt === 0 && retryDelaysMs.length > 1) notify.info(PANO_360_PICK_MESSAGES.WAITING);
    }
    if (load.source !== BORROWED_CLOUD_SOURCE) {
      notify.info(PANO_360_PICK_MESSAGES.STILL_LOADING);
      return emptyResult(autoLoadedPcName);
    }
  } else {
    await waitForRender(retryDelaysMs[0] ?? 0);
  }

  const point = pickModel();
  if (!point) {
    notify.info(PANO_360_PICK_MESSAGES.NO_SURFACE);
    return emptyResult(autoLoadedPcName);
  }
  notify.warning(PANO_360_PICK_MESSAGES.MODEL_FALLBACK);
  return { point, source: PANO_360_PICK_SOURCE.MODEL, autoLoadedPcName };
}
